import { Store } from 'vuex';
import { AccountStateStorable } from '@/shared/config/store/account-store';
import { RxStomp, RxStompConfig, RxStompState } from '@stomp/rx-stomp';
import VueRouter from 'vue-router';
import * as SockJS from 'sockjs-client';
import { filter, map, Observer, take, switchMap } from 'rxjs';
import { CmGroupsQueueMessage } from '@/shared/model/cm-group-queue-message.model';

const DESTINATION_TRACKER_NOTIFICATION = '/topic/notification';
const DESTINATION_TRACKER_UPDATE_FEED = '/topic/updateFeed';
const DESTINATION_TRACKER_UPDATE_CONNECTOME = '/topic/updateConnectome';
const DESTINATION_TRACKER_ISSUE_TRACKING = '/topic/signalTracking';
const DESTINATION_TRACKER_UPDATE_DISCOVERY = '/topic/news-discovery';
const DESTINATION_TRACKER_DISCOVERY_PREVIEW = '/topic/news-discovery-preview';
const DESTINATION_TRACKER_UPDATE_COLLECTION = '/topic/updateCollection';
const DESTINATION_TRACKER_CHAT = '/topic/chat';
const DESTINATION_TRACKER_SUMMARIZE = '/topic/summarize-selected-articles';

export default class WebsocketService {
  private rxStomp: RxStomp;
  private rxStompConfig: RxStompConfig;

  constructor(private router: VueRouter, private store: Store<AccountStateStorable>) {
    this.stomp = new RxStomp();
    // this.rxStompConfig = {
    //   connectHeaders: {
    //     login: 'deepsignal',
    //     passcode: 'deeppass22',
    //   }
    // };
    // this.stomp.configure(this.rxStompConfig);
    // this.router.afterEach(() => this.sendActivity());

    this.store.watch(
      (_state, getters) => getters.authenticated,
      (value, oldValue) => {
        if (value === oldValue) return;
        if (value) {
          return this.connect();
        }
        return this.disconnect();
      }
    );
  }

  get stomp() {
    return this.rxStomp;
  }

  set stomp(rxStomp) {
    this.rxStomp = rxStomp;
    this.rxStomp.configure({
      debug: (msg: string): void => {
        // console.log(new Date(), msg);
      },
    });
    // this.rxStomp.connected$.subscribe(() => {
    //   this.sendActivity();
    // });
  }

  private connect(): void {
    this.updateCredentials();
    return this.rxStomp.activate();
  }

  private disconnect(): Promise<void> {
    return this.rxStomp.deactivate();
  }

  private getAuthToken() {
    return localStorage.getItem('ds-authenticationToken') || sessionStorage.getItem('ds-authenticationToken');
  }

  private getUserId() {
    return localStorage.getItem('localStorageUser')
      ? JSON.parse(localStorage.getItem('localStorageUser'))?.id
      : JSON.parse(sessionStorage.getItem('localStorageUser'))?.id;
  }

  private buildUrl(): string {
    // building absolute path so that websocket doesn't fail when deploying with a context path
    const loc = window.location;
    const baseHref = document.querySelector('base').getAttribute('href');
    const url = '//' + loc.host + baseHref + 'websocket/tracker';
    const authToken = this.getAuthToken();
    if (authToken) {
      return `${url}?access_token=${authToken}`;
    }
    return url;
  }

  private updateCredentials(): void {
    this.rxStomp.configure({
      reconnectDelay: 2000,
      webSocketFactory: () => {
        return new SockJS(this.buildUrl());
      },
    });
  }

  // private sendActivity(): void {
  //   this.rxStomp.publish({
  //     destination: DESTINATION_ACTIVITY,
  //     body: JSON.stringify({ page: this.router.currentRoute.fullPath }),
  //   });
  // }
  // public subscribeNewsFeed(observer) {
  //   return this.rxStomp
  //     .watch(DESTINATION_TRACKER_UPDATE_DISCOVERY.concat(connnectomeId))
  //     .pipe(map(message => JSON.parse(message.body)))
  //     .subscribe(observer);
  // }

  public subscribeDiscoveryPreview(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_DISCOVERY_PREVIEW)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeNotification(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_NOTIFICATION)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeUpdateFeed(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_UPDATE_FEED)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeUpdateConnectome(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_UPDATE_CONNECTOME)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeSignalTracking(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_ISSUE_TRACKING)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeUpdateCollection(observer) {
    return this.rxStomp
      .watch(DESTINATION_TRACKER_UPDATE_COLLECTION)
      .pipe(map(message => new CmGroupsQueueMessage(JSON.parse(message.body))))
      .subscribe(observer);
  }

  public subscribeConversationChat(observer) {
    return this.rxStomp
      .watch('/topic/response/chat/' + this.getUserId())
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeSummarize(observer) {
    return this.rxStomp
      .watch('/topic/response/summarize/' + this.getUserId())
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingTitle(userKey: string, isOwner: boolean, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.title?owner=${isOwner}&userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingRecommendation(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.recommendation?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingResource(bpId: string, userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.resource?bpId=${bpId}&userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeScrapAgentData(bpId: string, userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/scrap.data?bpId=${bpId}&userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingHome(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.home?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingWidget(bpId: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.widget?bpId=${bpId}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public requestSummarize(body): void {
    this.rxStomp.publish({
      destination: DESTINATION_TRACKER_SUMMARIZE,
      body: JSON.stringify(body),
    });
  }

  public sendSentence(sentence): void {
    this.rxStomp.publish({
      destination: DESTINATION_TRACKER_CHAT,
      body: JSON.stringify(sentence),
    });
  }

  public subscribeReportData(reportData: string, userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/report_data?reportData=${reportData}&userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeBriefingDataForHome(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/briefing.resource.home?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeGetGoAuthWithChannel(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/go.auth?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeTokenAuth(userKey: string, observer?: Partial<Observer<any>>) {
    const topic$ = () =>
      this.rxStomp.watch(`/topic/scrap-agent-token-auth/${userKey}`).pipe(
        map(message => {
          try {
            return JSON.parse(message.body);
          } catch (e) {
            // If message.body is not valid JSON, return it as is
            return message.body;
          }
        })
      );
    if (!this.rxStomp.connected) {
      console.warn('WebSocket not connected. Waiting for connection...');
      return this.rxStomp.connected$
        .pipe(
          filter((state: RxStompState) => state === RxStompState.OPEN),
          take(1),
          switchMap(() => topic$())
        )
        .subscribe(observer);
    } else {
      return topic$().subscribe(observer);
    }
  }

  public subscribeGetPodcastSetting(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/podcast/setting?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }

  public subscribeGetRecapSetting(userKey: string, observer?: Partial<Observer<any>>) {
    return this.rxStomp
      .watch(`/topic/recap/setting?userKey=${userKey}`)
      .pipe(map(message => JSON.parse(message.body)))
      .subscribe(observer);
  }
}
